import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import api from "@/lib/axios"
import FormHeader from "@/components/FormHeader"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useState } from "react"
import { FaPlus } from "react-icons/fa6"
import { Loader2 } from "lucide-react"
import { JenisBerkas, JenjangPendidikan } from "@/types"
import { MdModeEdit } from "react-icons/md"
import { RiDeleteBack2Line } from "react-icons/ri"
import toast from "react-hot-toast"
import { AxiosError } from "axios"
import SkeletonRow from "@/components/SkeletonRow"
import { Checkbox } from "@/components/ui/checkbox"
import { getListJenjangPendidikan } from "@/services/getData"
import { Switch } from "@/components/ui/switch"

const postData = async (newData: JenisBerkas) => {
  const response = await api.post("/master/jenis-berkas/store", newData)
  return response.data
}

const patchData = async (newData: JenisBerkas) => {
  const response = await api.patch("/master/jenis-berkas/update", newData)
  return response.data
}

const deleteData = async (deletedData: { id_ref_jenis_berkas: string }) => {
  const response = await api({
    url: "/master/jenis-berkas/delete",
    method: "DELETE",
    data: deletedData,
  })
  return response.data
}

export default function Form({
  method,
  selectedRow,
  closeModal,
}: {
  method: "POST" | "PATCH" | "DELETE"
  selectedRow?: JenisBerkas | null
  closeModal: () => void
}) {
  const [form, setForm] = useState<JenisBerkas>({
    id_ref_jenis_berkas: selectedRow?.id_ref_jenis_berkas || "",
    nama_jenis_berkas: selectedRow?.nama_jenis_berkas || "",
    keterangan: selectedRow?.keterangan || "",
    untuk_daftar_jenjang: [...(selectedRow?.untuk_daftar_jenjang || [])],
    is_add_filed_tambahan: selectedRow?.is_add_filed_tambahan ?? false,
    slug_jenis_berkas: selectedRow?.slug_jenis_berkas || "",
  })

  const isPost = method === "POST"
  const isPatch = method === "PATCH"
  const isDelete = method === "DELETE"

  const {
    data: dataJenjangPendidikan,
    isPending: isPendingDataJenjangPendidikan,
  } = useQuery({
    queryKey: ["listJenjangPendidikan"],
    queryFn: getListJenjangPendidikan,
  })

  const queryClient = useQueryClient()
  const { mutate, isPending } = useMutation({
    mutationFn: isPost ? postData : isPatch ? patchData : deleteData,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["listJenisBerkas"] })
      closeModal()
      toast.success(data?.message || "Success.")
    },
    onError: (error: AxiosError) => {
      const err = error as AxiosError<{ message?: string }>
      toast.error(
        err?.response?.data?.message || "An unexpected error occurred."
      )
    },
  })

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    mutate(form)
  }

  if (isDelete)
    return (
      <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
        <FormHeader
          icon={<RiDeleteBack2Line />}
          title="Delete Data Jenis Berkas"
          description={`Apakah anda yakin akan menghapus data "${selectedRow?.nama_jenis_berkas}"?`}
        />

        <div className="flex gap-3">
          <Button
            type="button"
            variant={"outline"}
            disabled={isPending}
            onClick={() => closeModal()}
          >
            Batal
          </Button>
          <Button
            variant={"destructive"}
            className="flex items-center gap-3"
            disabled={isPending}
          >
            {isPending ? (
              <>
                <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                Processing...
              </>
            ) : (
              "Hapus"
            )}
          </Button>
        </div>
      </form>
    )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-y-3">
      <FormHeader
        icon={method === "POST" ? <FaPlus /> : <MdModeEdit />}
        title={isPost ? "Insert Data Jenis Berkas" : "Update Data Jenis Berkas"}
      />
      <section className="grid grid-cols-2 gap-3">
        <div>
          <Label>Nama Jenis Berkas</Label>
          <Input
            required
            placeholder="Ketik nama jenis berkas..."
            value={form.nama_jenis_berkas}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                nama_jenis_berkas: event.target.value,
              }))
            }
          />
        </div>
        <div>
          <Label>Keterangan</Label>
          <Input
            required
            placeholder="Ketik keterangan..."
            value={form.keterangan}
            onChange={(event) =>
              setForm((prev) => ({
                ...prev,
                keterangan: event.target.value,
              }))
            }
          />
        </div>
        {isPatch && (
          <div>
            <Label>Slug Jenis Berkas</Label>
            <Input
              required
              placeholder="Ketik keterangan..."
              value={form.slug_jenis_berkas}
              onChange={(event) =>
                setForm((prev) => ({
                  ...prev,
                  slug_jenis_berkas: event.target.value,
                }))
              }
            />
          </div>
        )}

        <div className="flex gap-3">
          <Switch
            id="is_add_filed_tambahan"
            checked={form.is_add_filed_tambahan}
            onCheckedChange={() =>
              setForm((prev) => ({
                ...prev,
                is_add_filed_tambahan: !prev.is_add_filed_tambahan,
              }))
            }
          />
          <Label htmlFor="is_add_filed_tambahan">
            Tambahkan Field Tambahan (tidak/ya)
          </Label>
        </div>
      </section>
      <div className="col-span-3">
        <Label>Untuk Jenjang:</Label>
        <div className="mt-3 grid grid-cols-4 gap-3">
          {isPendingDataJenjangPendidikan ? (
            Array(20)
              .fill(null)
              .map((_, index) => {
                return <SkeletonRow key={index} />
              })
          ) : (
            <>
              {dataJenjangPendidikan?.map(
                (jenjangPendidikan: JenjangPendidikan) => {
                  return (
                    <div
                      key={jenjangPendidikan.id_ref_jenjang_pendidikan}
                      className="flex items-center gap-3"
                    >
                      <Checkbox
                        required={form.untuk_daftar_jenjang.length == 0}
                        checked={form.untuk_daftar_jenjang?.some(
                          (jenjang: JenjangPendidikan) =>
                            jenjang.id_ref_jenjang_pendidikan ===
                            jenjangPendidikan.id_ref_jenjang_pendidikan
                        )}
                        onCheckedChange={() => {
                          setForm((prevForm) => {
                            const daftar_jenjang =
                              prevForm.untuk_daftar_jenjang || []

                            const isChecked = daftar_jenjang?.some(
                              (jenjang: JenjangPendidikan) =>
                                jenjang.id_ref_jenjang_pendidikan ==
                                jenjangPendidikan.id_ref_jenjang_pendidikan
                            )

                            const updatedDaftarJenjang = isChecked
                              ? daftar_jenjang.filter(
                                  (jenjang: JenjangPendidikan) =>
                                    jenjang.id_ref_jenjang_pendidikan !==
                                    jenjangPendidikan.id_ref_jenjang_pendidikan
                                )
                              : [...daftar_jenjang, jenjangPendidikan]

                            return {
                              ...prevForm,
                              untuk_daftar_jenjang: updatedDaftarJenjang,
                            }
                          })
                        }}
                      />
                      <Label>{jenjangPendidikan.nama_jenjang_pendidikan}</Label>
                    </div>
                  )
                }
              )}
            </>
          )}
        </div>
      </div>

      <Button className="mt-3 flex items-center gap-3" disabled={isPending}>
        {isPending ? (
          <>
            <Loader2 className="w-4 h-4 mr-1 animate-spin" />
            Processing...
          </>
        ) : (
          "Simpan"
        )}
      </Button>
    </form>
  )
}
